import { Box, Checkbox, Dialog, DialogContent } from '@mui/material';
import React, { useEffect } from 'react';
import { CustomDialogTitle } from '../../../../Common/Custom/CustomDialogTitle';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Deviations } from '../../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { DeviationsService } from '../../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService';
import { EEOccupationalLevel } from '../../../../../Component/Model/Enum/EmploymentEquity';
import { occupationalLevelDisplayNames } from '../../../Deviations/Shared/deviationsCommon';
import { Gender, RaceTypes } from '../../../../../Component/Model/Enum/BEE123Enums';
import { CoreStaticDataService } from '../../../../../Component/Api/StaticData/CoreStaticDataService';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { deviationSchema } from './DeviationValidation';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import AppSelectList, { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { PortalDocumentsDropZone } from '../../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { ConfirmationDialog } from '../../../../../Component/Common/Components/ConfirmationDialog';
import { ModalDialogActions } from '../../../../../Component/Common/Components/Modal/ModalDialogActions';
import { AuditDateDetails } from '../../../../../Component/Common/Components/Modal/AuditDateDetails';
import { toast } from 'react-toastify';
import { AppQuill } from "../../../../../Component/Common/Components/FormValidationInputs/AppQuill";
import colors from '../../../../../assets/theme/base/colors';

interface IProps {
	id?: number;
	occupationalLevel: EEOccupationalLevel;
	gender?: Gender;
	race?: RaceTypes;
	isDisabled: boolean;
	year?: number | null;
	scorecardId: string;
	showModal: boolean;
	titleContext?: string;
	handleCancel: () => void;
}

export const DeviationModal = (props: IProps) => {
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
	const [deviationId, setDeviationId] = React.useState<number | undefined>(props.id);
	const [deviationReasons, setDeviationReasons] = React.useState<IDropDownOptions[]>([]);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = React.useState<boolean>(false);
	const [goalAndTargetRowYearId, setGoalAndTargetRowYearId] = React.useState<number | null>(props.year ?? null);
	const [loadedDto, setLoadedDto] = React.useState<Deviations.DtoOut | null>(null);
	const commentMaxLength = 6000;
	const [remainingCommentCharacters, setRemainingCommentCharacters] = React.useState(commentMaxLength);
	const remedialPlanMaxLength = 1000;
	const [remainingRemedialPlanCharacters, setRemainingRemedialPlanCharacters] = React.useState(remedialPlanMaxLength);

	const { handleSubmit, control, reset, formState: { errors } } = useForm({
		mode: 'all',
		resolver: yupResolver(deviationSchema)
	});

	const getDeviationReasons = async () => {
		try {
			const result = await CoreStaticDataService.getGroupedDeviationReasons();
			const regulatory = result.data.find(g => g.group === 'Regulatory Reasons');
			setDeviationReasons(
				(regulatory?.items ?? []).map((item) => ({ id: item.id, value: item.name }))
			);
		} catch (error) {
			console.log(error);
		}
	};

	const getById = async (id: number) => {
		try {
			const result = await DeviationsService.GetById(props.scorecardId, id);
			setGoalAndTargetRowYearId(result.data.goalAndTargetRowYearId);
			setLoadedDto(result.data);
			reset(result.data);
		} catch (error) {
			console.log(error);
		}
	};

	const handleFormError = () => {
		const form = document.getElementById('deviationsForm');
		if (!form) return;
		const firstInvalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
		if (firstInvalid) {
			firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} else {
			const dialogContent = form.closest<HTMLElement>('.MuiDialogContent-root');
			if (dialogContent) dialogContent.scrollTop = 0;
		}
	};

	const deleteDeviation = async () => {
		setIsDeleting(true);
		try {
			if (deviationId) {
				await DeviationsService.Delete(props.scorecardId, deviationId);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsDeleting(false);
			handleCloseConfirmation();
			props.handleCancel();
		}
	};

	const handleDataSubmit: SubmitHandler<FieldValues> = async (data: FieldValues) => {
		setIsLoading(true);
		try {
			const dtoIn: Deviations.DtoIn = {
				scorecardId: props.scorecardId,
				eEOccupationLevelId: props.occupationalLevel as number,
				raceId: props.race != null ? props.race as number : null,
				genderId: props.gender != null ? props.gender as number : null,
				isDisabled: props.isDisabled,
				goalAndTargetRowYearId: goalAndTargetRowYearId,
				reasonId: data.reasonId,
				comment: data.comment,
				eEA2KeyJustifiableReason: data.eEA2KeyJustifiableReason ?? false,
				remedialPlan: data.remedialPlan,
			};
			if (deviationId == undefined) {
				const result = await DeviationsService.Create(dtoIn);
				reset(result.data);
				setDeviationId(result.data.id);
				setLoadedDto(result.data);
				toast.success('Deviation created successfully');
				setTimeout(() => {
					const form = document.getElementById('deviationsForm');
					const dialogContent = form?.closest<HTMLElement>('.MuiDialogContent-root');
					if (dialogContent) {
						dialogContent.scrollTop = dialogContent.scrollHeight;
					}
				}, 300);
			} else {
				dtoIn.id = deviationId;
				const result = await DeviationsService.Update(dtoIn);
				reset(result.data);
				setLoadedDto(result.data);
				toast.success('Deviation updated successfully');
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDelete = () => {
		setShowDeleteConfirmationDialog(true);
	};

	const handleCloseConfirmation = () => {
		setShowDeleteConfirmationDialog(false);
	};

	useEffect(() => {
		setIsLoading(true);
		getDeviationReasons();
		if (deviationId != undefined) {
			getById(deviationId);
		} else {
			setLoadedDto(null);
		}
		setIsLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deviationId]);

	return (
		<>
			<Dialog disableEnforceFocus open={props.showModal} onClose={props.handleCancel} aria-labelledby='deviationDialogTitle' maxWidth='md' fullWidth scroll='paper'>
				<CustomDialogTitle id='deviationDialogTitle' onClose={props.handleCancel}>
					{deviationId == undefined ? 'Add' : 'Edit'} Justification Reason - {props.titleContext ?? occupationalLevelDisplayNames[props.occupationalLevel]}
				</CustomDialogTitle>
				<DialogContent>
					{deviationId == undefined && (
						<MDTypography variant='caption' sx={{ display: 'block', mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
							You will be able to upload supporting documents after completing the form.
						</MDTypography>
					)}
					{isLoading ? (
						<SmallLoading />
					) : (
						<Box component='form' id='deviationsForm' onSubmit={handleSubmit(handleDataSubmit, handleFormError)} sx={{ mt: 1 }}>
							<MDBox mb={2}>
								<AppSelectList
									control={control}
									name='reasonId'
									label='Justifiable Reason*'
									items={deviationReasons}
									size={'small'}
								/>
							</MDBox>
							<MDBox mb={2}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: '12px',
										backgroundColor: colors.ui.eea2CardBackground,
										border: `0.8px solid ${colors.ui.eea2CardBorder}`,
										borderRadius: '10px',
										pt: '14px',
										pb: 2,
										px: 2,
									}}
								>
									<Controller
										control={control}
										name='eEA2KeyJustifiableReason'
										render={({ field }) => (
											<Checkbox
												{...field}
												checked={!!field.value}
												id='eEA2KeyJustifiableReason'
												sx={{
													p: 0,
													mt: '2px',
													width: 20,
													height: 20,
													color: colors.ui.activeBackground,
													'&.Mui-checked': { color: colors.ui.activeBackground },
													flexShrink: 0,
												}}
											/>
										)}
									/>
									<Box
										component='label'
										htmlFor='eEA2KeyJustifiableReason'
										sx={{ flex: 1, cursor: 'pointer', mb: 0 }}
									>
										<MDTypography
											variant='button'
											sx={{ display: 'block', fontSize: '14px', fontWeight: 600, color: colors.dark.main, mb: '4px' }}
										>
											Mark as EEA2 Key Justifiable Reason
										</MDTypography>
										<MDTypography
											variant='caption'
											sx={{ display: 'block', color: colors.dark.main, fontSize: '12px', mb: 0 }}
										>
											Only one justification per occupational level can be included in the generated EEA2 report. Selecting this will automatically unmark any existing EEA2 key justifiable reason.
										</MDTypography>
									</Box>
								</Box>
							</MDBox>
							<MDBox mb={2}>
								<AppQuill control={control} name='comment' label='Commentary*' maxLength={commentMaxLength} />
							</MDBox>
							<MDBox mb={2}>
								<AppQuill control={control} name='remedialPlan' label='Remedial Plan' maxLength={remedialPlanMaxLength} />
							</MDBox>
							{deviationId != undefined && (
								<MDBox mt={2} mb={2}>
									<PortalDocumentsDropZone id={deviationId} link={'deviation'} />
								</MDBox>
							)}
							{loadedDto && (
								<AuditDateDetails
									heading='Justification Date Details'
									createdDateUTC={loadedDto.createdDate}
									createdByName={loadedDto.createdBy}
									lastModifiedDateUTC={loadedDto.lastModifiedDate}
									lastModifiedByName={loadedDto.lastModifiedBy}
								/>
							)}
						</Box>
					)}
				</DialogContent>
				<ModalDialogActions
					formId='deviationsForm'
					isSubmitting={isLoading}
					onCancel={props.handleCancel}
					showDelete={deviationId != undefined}
					onDelete={handleDelete}
				/>
			</Dialog>
			<ConfirmationDialog
				showDialog={showDeleteConfirmationDialog}
				handleAccept={deleteDeviation}
				handleCancel={handleCloseConfirmation}
				heading={'Delete Deviation'}
				message={'Are you sure you want to delete this deviation and associated files?'}
				isLoading={isDeleting}
			/>
		</>
	);
};
